import React, {
  createContext,
  useContext,
  useId,
  useRef,
  useMemo,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react';
import './Citations.css';

/*
 * LaTeX-style citations.
 *
 * Define a references map (your "BibTeX file") and cite entries inline by key:
 *
 *   const references = {
 *     vaswani2017: {
 *       authors: ['Ashish Vaswani', 'Noam Shazeer', 'Niki Parmar'],
 *       title: 'Attention Is All You Need',
 *       venue: 'NeurIPS',
 *       year: 2017,
 *       url: 'https://arxiv.org/abs/1706.03762',
 *     },
 *     // A plain string also works (rendered verbatim in the bibliography):
 *     someBlog: 'Some Author. "A Blog Post." 2024. https://example.com',
 *   };
 *
 *   <ProjectLayout project={project} references={references}>
 *     <p>Transformers<Cite id="vaswani2017" /> changed the field.</p>
 *     <p><Citet id="vaswani2017" /> showed that attention suffices.</p>
 *     <p>Several works<Cite id="vaswani2017, someBlog" /> agree.</p>
 *   </ProjectLayout>
 *
 * Like \cite{a,b}, an id with comma-separated keys cites them all in one
 * block: [1, 2]. An array id={['a', 'b']} works too. <Citep> is an alias of
 * <Cite> (citations are numeric-bracketed, so \citep and \cite coincide).
 *
 * <Cite> renders a numeric marker like [1] (\cite); <Citet> renders
 * "Vaswani et al. [1]" (\citet). Numbers are assigned by order of first
 * citation in the document. Hovering a marker shows the full reference in a
 * tooltip, clicking it jumps to the bibliography, and hovering either end
 * highlights the other. <Bibliography /> lists only the entries actually
 * cited — ProjectLayout renders it automatically when given a `references`
 * prop, so project pages never place it by hand.
 *
 * Entry fields: authors (array or string), title, venue, year, url, note.
 * Optional `short` overrides the name <Citet> uses (also the only way to get
 * a good <Citet> label out of a plain-string entry).
 *
 * Instead of a map you can paste raw BibTeX as a template literal — the
 * provider parses it and the citation keys are the BibTeX keys:
 *
 *   const references = `
 *     @inproceedings{vaswani2017,
 *       author    = {Vaswani, Ashish and Shazeer, Noam and others},
 *       title     = {Attention Is All You Need},
 *       booktitle = {NeurIPS},
 *       year      = {2017},
 *       url       = {https://arxiv.org/abs/1706.03762},
 *     }
 *   `;
 *
 * journal/booktitle become the venue; arXiv eprint fields produce both the
 * venue and the link when url is omitted (doi works too); a custom `short`
 * field carries through for <Citet>.
 */

/* ---------------- BibTeX parsing ---------------- */

// De-TeX a field value: unescape special chars, drop styling commands and the
// braces BibTeX uses to protect capitalization.
const cleanTex = (s) =>
  s
    .replace(/\\([&%$#_])/g, '$1')
    .replace(/\\(emph|textit|textbf|textsc|texttt|mkbibquote|url)\b/g, '')
    .replace(/[{}]/g, '')
    .replace(/~/g, ' ')
    .replace(/---/g, '—')
    .replace(/--/g, '–')
    .replace(/\s+/g, ' ')
    .trim();

// Parse the `field = value, field = value` body of one entry. Values are
// {balanced braces}, "quoted", or bare (numbers).
const parseBibtexFields = (body) => {
  const fields = {};
  let i = 0;
  const n = body.length;
  while (i < n) {
    while (i < n && /[\s,]/.test(body[i])) i++;
    let start = i;
    while (i < n && /[\w-]/.test(body[i])) i++;
    const name = body.slice(start, i).toLowerCase();
    if (!name) break;
    while (i < n && /\s/.test(body[i])) i++;
    if (body[i] !== '=') break;
    i++;
    while (i < n && /\s/.test(body[i])) i++;
    let value;
    if (body[i] === '{') {
      let depth = 1;
      start = ++i;
      while (i < n && depth > 0) {
        if (body[i] === '{') depth++;
        else if (body[i] === '}') depth--;
        i++;
      }
      value = body.slice(start, i - 1);
    } else if (body[i] === '"') {
      start = ++i;
      while (i < n && body[i] !== '"') i++;
      value = body.slice(start, i);
      i++;
    } else {
      start = i;
      while (i < n && body[i] !== ',') i++;
      value = body.slice(start, i);
    }
    fields[name] = cleanTex(value);
  }
  return fields;
};

// BibTeX authors: "Last, First and First Last and others" -> display names.
const parseBibtexAuthors = (author) =>
  author.split(/\s+and\s+/i).map((a) => {
    if (/^others$/i.test(a.trim())) return 'et al.';
    const parts = a.split(',');
    return parts.length === 2
      ? `${parts[1].trim()} ${parts[0].trim()}`
      : a.trim();
  });

// One BibTeX entry's fields -> the reference shape <Bibliography> renders.
const entryFromBibtex = (fields) => {
  const eprint = fields.eprint;
  const isArxiv =
    /arxiv/i.test(
      `${fields.archiveprefix || ''} ${fields.eprinttype || ''} ${
        fields.journal || ''
      }`
    ) ||
    (eprint && /^\d{4}\.\d{4,5}/.test(eprint));
  let venue =
    fields.journal ||
    fields.booktitle ||
    fields.school ||
    fields.institution ||
    fields.howpublished;
  if (!venue && isArxiv && eprint) venue = `arXiv preprint arXiv:${eprint}`;
  const url =
    fields.url ||
    (isArxiv && eprint
      ? `https://arxiv.org/abs/${eprint}`
      : fields.doi
      ? `https://doi.org/${fields.doi}`
      : undefined);
  const entry = {
    authors: fields.author ? parseBibtexAuthors(fields.author) : undefined,
    title: fields.title,
    venue,
    year: fields.year,
    url,
    note: fields.note,
    short: fields.short,
  };
  Object.keys(entry).forEach((k) => entry[k] === undefined && delete entry[k]);
  return entry;
};

// Parse a string of BibTeX entries into a references map keyed by cite key.
export const parseBibtex = (src) => {
  const refs = {};
  const head = /@(\w+)\s*\{\s*([^,\s{}]+)\s*,/g;
  let m;
  while ((m = head.exec(src))) {
    const type = m[1].toLowerCase();
    // Find the matching closing brace of this entry.
    let depth = 1;
    let i = head.lastIndex;
    while (i < src.length && depth > 0) {
      if (src[i] === '{') depth++;
      else if (src[i] === '}') depth--;
      i++;
    }
    const body = src.slice(head.lastIndex, i - 1);
    head.lastIndex = i;
    if (type === 'comment' || type === 'string' || type === 'preamble') continue;
    refs[m[2]] = entryFromBibtex(parseBibtexFields(body));
  }
  return refs;
};

const CitationContext = createContext(null);

// useId() returns ids containing ':' which are not valid in URL fragments or
// querySelector — strip them for use in DOM ids / hrefs.
const safeId = (id) => `cite-${id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

// Smooth-scroll to a same-page target instead of the default instant jump.
const jumpTo = (e, targetId) => {
  const el = document.getElementById(targetId);
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const lastName = (name) => name.trim().split(/\s+/).pop();

// The label <Citet> uses: "Vaswani", "Vaswani and Shazeer", "Vaswani et al."
const shortAuthors = (entry, key) => {
  if (!entry) return key;
  if (typeof entry === 'string') return key;
  if (entry.short) return entry.short;
  const authors = Array.isArray(entry.authors)
    ? entry.authors
    : entry.authors
    ? [entry.authors]
    : [];
  if (authors.length === 0) return entry.title || key;
  if (authors.length === 1) return lastName(authors[0]);
  if (authors.length === 2)
    return `${lastName(authors[0])} and ${lastName(authors[1])}`;
  return `${lastName(authors[0])} et al.`;
};

// A reference formatted IEEE-ish: Authors. "Title." Venue, year. Note.
const ReferenceText = ({ entry, linked = true }) => {
  if (typeof entry === 'string') return <>{entry}</>;
  const authors = Array.isArray(entry.authors)
    ? entry.authors.join(', ')
    : entry.authors;
  const title = entry.title && `“${entry.title}.”`;
  return (
    <>
      {authors && <>{authors}. </>}
      {title &&
        (entry.url && linked ? (
          <a
            className="bib-title"
            href={entry.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {title}
          </a>
        ) : (
          <span className="bib-title">{title}</span>
        ))}
      {entry.venue && (
        <>
          {' '}
          <em>{entry.venue}</em>
          {entry.year ? ',' : '.'}
        </>
      )}
      {entry.year && <> {entry.year}.</>}
      {entry.note && <> {entry.note}</>}
    </>
  );
};

export const CitationProvider = ({ references: referencesProp = {}, children }) => {
  // The references prop is either an entry map or a raw BibTeX string.
  const references = useMemo(
    () =>
      typeof referencesProp === 'string'
        ? parseBibtex(referencesProp)
        : referencesProp,
    [referencesProp]
  );
  // Live registry of every citation site: instanceId -> { id, node, keys }.
  // Kept in a ref so registering doesn't churn identity; the derived citation
  // order is mirrored into state to drive numbering and the bibliography.
  const sitesRef = useRef(new Map());
  const [cited, setCited] = useState({ keys: [], firstSite: {} });
  const [hovered, setHovered] = useState([]);

  const recompute = useCallback(() => {
    const sites = [...sitesRef.current.values()];
    // Sort by actual document position so numbering matches reading order,
    // regardless of mount timing.
    sites.sort((a, b) => {
      if (!a.node || !b.node || a.node === b.node) return 0;
      const pos = a.node.compareDocumentPosition(b.node);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
    const seen = new Set();
    const keys = [];
    const firstSite = {};
    sites.forEach((site) => {
      site.keys.forEach((key) => {
        if (seen.has(key)) return;
        seen.add(key);
        keys.push(key);
        // Anchor id of the first place this key is cited, so the bibliography
        // number can link back into the text.
        firstSite[key] = `${site.id}-${key}`;
      });
    });
    setCited({ keys, firstSite });
  }, []);

  const register = useCallback(
    (id, node, keys) => {
      sitesRef.current.set(id, { id, node, keys });
      recompute();
    },
    [recompute]
  );

  const unregister = useCallback(
    (id) => {
      sitesRef.current.delete(id);
      recompute();
    },
    [recompute]
  );

  // key -> citation number, in order of first appearance.
  const numbers = useMemo(() => {
    const map = {};
    cited.keys.forEach((key, i) => {
      map[key] = i + 1;
    });
    return map;
  }, [cited]);

  const ctx = useMemo(
    () => ({
      references,
      register,
      unregister,
      cited,
      numbers,
      hovered,
      setHovered,
    }),
    [references, register, unregister, cited, numbers, hovered]
  );

  return (
    <CitationContext.Provider value={ctx}>{children}</CitationContext.Provider>
  );
};

export const Cite = ({ id, mode = 'p' }) => {
  const ctx = useContext(CitationContext);
  const rawId = useId();
  const instanceId = safeId(rawId);
  const markerRef = useRef(null);
  // id is an array or a string; a string may hold several comma-separated
  // keys, like \cite{a, b}.
  const keys = (Array.isArray(id) ? id : String(id).split(','))
    .map((k) => String(k).trim())
    .filter(Boolean);
  // Keep the latest keys available to the registry without re-registering on
  // every render.
  const keysRef = useRef(keys);
  keysRef.current = keys;

  useLayoutEffect(() => {
    if (!ctx) return undefined;
    ctx.register(instanceId, markerRef.current, keysRef.current);
    keysRef.current.forEach((key) => {
      if (!ctx.references[key]) {
        // eslint-disable-next-line no-console
        console.warn(`<Cite>: unknown reference key "${key}"`);
      }
    });
    return () => ctx.unregister(instanceId);
    // register/unregister are stable; a cite's keys never change in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!ctx) return null;
  const { references, numbers, hovered, setHovered } = ctx;

  const hoverProps = {
    onMouseEnter: () => setHovered(keys),
    onMouseLeave: () => setHovered([]),
  };

  const numberLink = (key) => {
    const entry = references[key];
    const active = hovered.includes(key);
    return (
      <a
        id={`${instanceId}-${key}`}
        href={`#bib-${key}`}
        className={`cite-link${active ? ' cite-active' : ''}${
          entry ? '' : ' cite-missing'
        }`}
        onClick={(e) => jumpTo(e, `bib-${key}`)}
      >
        {entry ? numbers[key] : '?'}
      </a>
    );
  };

  const tooltip = (
    <span className="cite-tooltip" role="tooltip">
      {keys.map((key) => (
        <span key={key} className="cite-tooltip-entry">
          {references[key] ? (
            <ReferenceText entry={references[key]} linked={false} />
          ) : (
            <>Unknown reference “{key}”.</>
          )}
        </span>
      ))}
    </span>
  );

  if (mode === 't') {
    // \citet: "Vaswani et al. [1]" — usable as the subject of a sentence.
    return (
      <span className="cite cite-textual" ref={markerRef} {...hoverProps}>
        {keys.map((key, i) => (
          <React.Fragment key={key}>
            {i > 0 && '; '}
            {shortAuthors(references[key], key)} [{numberLink(key)}]
          </React.Fragment>
        ))}
        {tooltip}
      </span>
    );
  }

  // \cite: "[1]" or "[1, 3]".
  return (
    <span className="cite" ref={markerRef} {...hoverProps}>
      [
      {keys.map((key, i) => (
        <React.Fragment key={key}>
          {i > 0 && ', '}
          {numberLink(key)}
        </React.Fragment>
      ))}
      ]{tooltip}
    </span>
  );
};

export const Citet = (props) => <Cite {...props} mode="t" />;

// \citep — with numeric-bracketed citations this is the same as \cite.
export const Citep = (props) => <Cite {...props} mode="p" />;

export const Bibliography = ({ title = 'References' }) => {
  const ctx = useContext(CitationContext);
  if (!ctx) return null;
  const { references, cited, numbers, hovered, setHovered } = ctx;
  const keys = cited.keys.filter((key) => references[key]);
  // The heading renders even while the list is still empty so the layout's
  // table of contents (which scans the DOM on first paint) picks it up.
  return (
    <section className="bibliography">
      <h2>{title}</h2>
      {keys.length > 0 && (
        <ol className="bibliography-list" aria-label={title}>
          {keys.map((key) => (
            <li
              key={key}
              id={`bib-${key}`}
              className={`bib-entry${
                hovered.includes(key) ? ' bib-active' : ''
              }`}
              onMouseEnter={() => setHovered([key])}
              onMouseLeave={() => setHovered([])}
            >
              <a
                className="bib-number"
                href={`#${cited.firstSite[key]}`}
                onClick={(e) => jumpTo(e, cited.firstSite[key])}
                title="Back to citation"
              >
                [{numbers[key]}]
              </a>
              <span className="bib-text">
                <ReferenceText entry={references[key]} />
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

export default Cite;
